import { RevxMoney } from 'components/RevxMoney';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { Form, Input, Space, Upload } from 'antd';
import { UPLOAD_ACCEPT, WORKSPACE_DELAY_TIME } from 'utils/constants';
import { RevxButton } from 'components/RevxButton';
import { UploadOutlined } from '@ant-design/icons';
import { JobItem } from 'domains/workspaceDomain';
import { useState } from 'react';
import { SupplierInvoiceFile } from 'domains/workspaceDetailDomain';
import jobService from 'services/jobService';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import uploadFileService from 'services/uploadService';
import { UploadFile } from 'antd/lib/upload/interface';
import { newFileObject, sleep } from 'utils/misc';
import { useAppDispatch } from 'app/hooks';
import { useGetJobCountHandler } from 'hooks';
import { useTranslation } from 'react-i18next';
import { UnderlineText } from 'components/RevxTypography';
import { useMount } from 'ahooks';

type CardActionUploadInvoiceProps = {
  data: JobItem;
};

const CardActionUploadInvoice = ({ data }: CardActionUploadInvoiceProps) => {
  const [invoiceFileList, setInvoiceFileList] = useState<UploadFile[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [submitSuccessStatus, setSubmitSuccessStatus] = useState(false);

  const [invoiceForm] = Form.useForm();
  const dispatch = useAppDispatch();
  const refreshJobCount = useGetJobCountHandler();
  const { t } = useTranslation();

  const {
    id,
    supplierPrintingCharge,
    supplierFreightCharge,
    supplierCharge,
    invoice: { supplierInvoiceNumber, supplierInvoiceReferenceFiles },
  } = data;

  useMount(() => {
    if (supplierInvoiceNumber && !!supplierInvoiceReferenceFiles.length) {
      const { name, path } = supplierInvoiceReferenceFiles[0];
      setSubmitSuccessStatus(true);
      invoiceForm.setFieldsValue({
        invoiceNumber: supplierInvoiceNumber,
        invoiceFileUrl: name,
      });
      setInvoiceFileList([newFileObject(name, path, id, false)]);
    }
  });

  const requestPaymentHandler = async () => {
    await dispatch.app.showLoading();
    await jobService.sendInvoiceToRevshop(id);
    await sendSuccess();
  };

  const uploadHandler = async (option: UploadRequestOption) => {
    setUploadLoading(true);
    dispatch.app.setState({
      isUploading: true,
    });
    const ret = await uploadFileService.uploadFile({
      file: option.file as File,
    });
    setInvoiceFileList([option.file as UploadFile]);
    invoiceForm.setFieldsValue({
      invoiceFileUrl: new URL(ret!.path).pathname.replace(/^\//, ''),
    });
    dispatch.app.setState({
      isUploading: false,
    });
    setUploadLoading(false);
  };

  const sendSuccess = async () => {
    await sleep(WORKSPACE_DELAY_TIME);
    await dispatch.alert.getNotificationAndAlerts();
    dispatch.workspace.reloadJobList();
    refreshJobCount();
    setInvoiceFileList([]);
    await dispatch.app.hideLoading();
  };

  const triggerFormValidate = () => {
    invoiceForm.validateFields().then(async (values) => {
      const { invoiceNumber, invoiceFileUrl } = values;
      const v: SupplierInvoiceFile = {
        invoiceNumber,
        file: {
          name: invoiceFileList[0]?.name || '',
          path: invoiceFileUrl,
        },
      };
      await dispatch.app.showLoading();
      await jobService.submitInvoice([{ jobId: id, invoice: v }]);
      await dispatch.app.hideLoading();
      setSubmitSuccessStatus(true);
    });
  };

  return (
    <Space direction="vertical" className="upload-invoice-file">
      <div className="job-charges-wrap">
        <h3>{submitSuccessStatus ? 'You submitted:' : 'Job Charges'}</h3>
        {!submitSuccessStatus && (
          <>
            <p>
              Printing: <RevxMoney value={supplierPrintingCharge} />
            </p>
            <p>
              Freight: <RevxMoney value={supplierFreightCharge} />
            </p>
          </>
        )}
        {submitSuccessStatus ? (
          <div>
            <div>Invoice: {invoiceForm.getFieldValue('invoiceNumber')}</div>
            <div>
              for <RevxMoney value={supplierCharge} />
            </div>
          </div>
        ) : (
          <div>
            Invoice Total: <RevxMoney value={supplierCharge} />
          </div>
        )}
      </div>
      {!submitSuccessStatus ? (
        <>
          <div className="invoice-block">
            <RevxFlexLayout
              justify="justify-center"
              className="invoice-block__form"
            >
              <Form form={invoiceForm}>
                <Form.Item
                  name="invoiceNumber"
                  rules={[
                    { required: true, message: 'Please enter invoice number.' },
                  ]}
                >
                  <Input placeholder="Enter invoice number" />
                </Form.Item>
                <Form.Item
                  name="invoiceFileUrl"
                  rules={[
                    {
                      required: true,
                      message: 'Please upload invoice file.',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (value && uploadLoading) {
                          return Promise.reject(
                            'File upload is in progress. Please wait.'
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <RevxFlexLayout
                    justify="justify-center"
                    className="invoice-block__form__upload"
                  >
                    <Upload
                      accept={UPLOAD_ACCEPT}
                      maxCount={1}
                      multiple={false}
                      fileList={invoiceFileList}
                      customRequest={uploadHandler}
                      onRemove={() => {
                        setInvoiceFileList([]);
                        invoiceForm.setFieldsValue({
                          invoiceFileUrl: undefined,
                        });
                      }}
                    >
                      <RevxFlexLayout justify="justify-center">
                        {!invoiceFileList.length && (
                          <RevxButton
                            loading={uploadLoading}
                            icon={<UploadOutlined />}
                          >
                            Upload invoice
                          </RevxButton>
                        )}
                      </RevxFlexLayout>
                    </Upload>
                  </RevxFlexLayout>
                </Form.Item>
              </Form>
            </RevxFlexLayout>
          </div>
          <RevxFlexLayout justify="justify-center">
            <RevxButton
              type="primary"
              onClick={triggerFormValidate}
              className="submit-btn"
            >
              SUBMIT
            </RevxButton>
          </RevxFlexLayout>
        </>
      ) : (
        <>
          <RevxFlexLayout justify="justify-center">
            <RevxButton
              type="primary"
              size="large"
              onClick={async (e) => {
                await requestPaymentHandler();
              }}
            >
              {t('workspace.list.requestPayment')}
            </RevxButton>
          </RevxFlexLayout>
          <RevxFlexLayout justify="justify-center">
            <UnderlineText
              color="blue"
              onClick={() => {
                setSubmitSuccessStatus(false);
              }}
            >
              Cancel and update
            </UnderlineText>
          </RevxFlexLayout>
        </>
      )}
    </Space>
  );
};
export default CardActionUploadInvoice;
